import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Spinner } from "components";
import {
  downloadPayoutCsv,
  listPayoutPartners,
  markPayout,
  payoutReport,
  type PayoutFilters,
  type PayoutRow,
} from "service/moneeflo/reports-api";

/** Paise to rupees. Payout figures are read down a column, so no symbol. */
function rupees(minor: string | null | undefined): string {
  if (minor === null || minor === undefined) return "-";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(minor) / 100);
}

function fcy(minor: string | null, currency: string | null): string {
  if (!minor) return "-";
  const amount = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(Number(minor) / 100);
  return currency ? `${currency} ${amount}` : amount;
}

/**
 * Where an order number points. The detail screens take the number with
 * the house prefix off — MF-R005044 is reached as R005044 — and forex
 * orders live on their own route.
 */
function orderHref(row: PayoutRow): string {
  const ref = row.orderNo.replace(/^(ORD-)?MF-/, "");
  return row.category === "forex"
    ? `/forex/orderdetails/${ref}`
    : `/orderdetails/${ref}`;
}

function day(at: string | null): string {
  if (!at) return "-";
  return new Date(at).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

/** The first of the current month and today, as the date inputs want them. */
function defaultWindow(): { from: string; till: string } {
  const ist = new Date(Date.now() + 330 * 60_000).toISOString().slice(0, 10);
  return { from: `${ist.slice(0, 7)}-01`, till: ist };
}

const ORDER_STATUSES = [
  "confirmed",
  "processing",
  "fulfilled",
  "on_hold",
  "cancelled",
  "refunded",
];

export default function PayoutReport(): JSX.Element {
  const initial = useMemo(defaultWindow, []);
  const [filters, setFilters] = useState<PayoutFilters>({
    ...initial,
    page: 1,
    perPage: 50,
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const queryClient = useQueryClient();
  /**
   * Everything the table is filtered by except the partner itself, so
   * the dropdown lists exactly the partners the rows below belong to.
   */
  const partnerScope = useMemo(() => {
    const { partnerId: _ignored, page: _p, perPage: _pp, ...rest } = filters;
    return rest;
  }, [filters]);

  const partners = useQuery(
    ["payout", "partners", partnerScope],
    () => listPayoutPartners(partnerScope),
    { keepPreviousData: true, staleTime: 60 * 1000 },
  );
  const report = useQuery(
    ["payout", "report", filters],
    () => payoutReport(filters),
    { keepPreviousData: true },
  );

  const mark = useMutation(markPayout, {
    onSuccess: (result) => {
      setSelected(new Set());
      setNotice(
        result.skipped > 0
          ? `Updated ${result.updated}. ${result.skipped} were not yours to change.`
          : `Updated ${result.updated}.`,
      );
      void queryClient.invalidateQueries(["payout", "report"]);
    },
    onError: () => setNotice("Could not save that. Nothing was changed."),
  });

  /** Any filter change puts us back on page one; page 23 of a new query is nothing. */
  function setFilter(patch: Partial<PayoutFilters>) {
    setSelected(new Set());
    setNotice(null);
    setFilters((f) => ({ ...f, ...patch, page: patch.page ?? 1 }));
  }

  async function download() {
    setDownloading(true);
    setNotice(null);
    try {
      await downloadPayoutCsv(filters);
    } catch {
      setNotice("Could not build the download. Nothing was changed.");
    } finally {
      setDownloading(false);
    }
  }

  const rows = report.data?.items ?? [];
  // Present only for a caller allowed to see what we make.
  const showsMyProfit = rows.some((r) => r.myProfitMinor !== undefined);
  const allOnPageSelected =
    rows.length > 0 && rows.every((r) => selected.has(r.id));

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllOnPage() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) rows.forEach((r) => next.delete(r.id));
      else rows.forEach((r) => next.add(r.id));
      return next;
    });
  }

  const summary = report.data?.summary;
  const partnerName = filters.partnerId
    ? partners.data?.find((p) => p.id === filters.partnerId)?.name
    : null;
  const lastPage = Math.max(
    1,
    Math.ceil((report.data?.total ?? 0) / (filters.perPage ?? 50)),
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border bg-white-A700 p-4">
        <label className="flex flex-col gap-1 text-xs text-gray-500">
          From
          <input
            type="date"
            id="payout-from"
            value={filters.from ?? ""}
            max={filters.till}
            onChange={(e) => setFilter({ from: e.target.value })}
            className="rounded-lg border px-3 py-2 text-sm text-[#333333]"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-500">
          Till
          <input
            type="date"
            id="payout-till"
            value={filters.till ?? ""}
            min={filters.from}
            onChange={(e) => setFilter({ till: e.target.value })}
            className="rounded-lg border px-3 py-2 text-sm text-[#333333]"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-500">
          Partner
          <select
            id="payout-partner"
            value={filters.partnerId ?? ""}
            onChange={(e) =>
              setFilter({ partnerId: e.target.value || undefined })
            }
            className="w-56 rounded-lg border px-3 py-2 text-sm text-[#333333]"
          >
            <option value="">Every partner</option>
            {(partners.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.city ? ` — ${p.city}` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-500">
          Product
          <select
            id="payout-category"
            value={filters.category ?? ""}
            onChange={(e) =>
              setFilter({ category: e.target.value || undefined })
            }
            className="rounded-lg border px-3 py-2 text-sm text-[#333333]"
          >
            <option value="">All products</option>
            <option value="remittance">Remittance</option>
            <option value="forex">Forex</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-500">
          Order status
          <select
            id="payout-status"
            value={filters.status ?? ""}
            onChange={(e) => setFilter({ status: e.target.value || undefined })}
            className="rounded-lg border px-3 py-2 text-sm text-[#333333]"
          >
            <option value="">Every real order</option>
            {ORDER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replace("_", " ")}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-500">
          Settlement
          <select
            id="payout-settlement"
            value={
              filters.paid === true
                ? "paid"
                : filters.paid === false
                  ? "unpaid"
                  : filters.billed === true
                    ? "billed"
                    : filters.billed === false
                      ? "unbilled"
                      : ""
            }
            onChange={(e) => {
              const v = e.target.value;
              setFilter({
                paid: v === "paid" ? true : v === "unpaid" ? false : undefined,
                billed:
                  v === "billed" ? true : v === "unbilled" ? false : undefined,
              });
            }}
            className="rounded-lg border px-3 py-2 text-sm text-[#333333]"
          >
            <option value="">Everything</option>
            <option value="unbilled">Not billed</option>
            <option value="billed">Billed</option>
            <option value="unpaid">Not paid</option>
            <option value="paid">Paid</option>
          </select>
        </label>
      </div>

      {/* Headline figures */}
      <div className="flex flex-wrap gap-4">
        <figure className="flex min-w-[200px] flex-1 flex-col gap-1 rounded-xl border bg-white-A700 p-5">
          <figcaption className="text-sm text-gray-600_01">
            Commission lines
          </figcaption>
          <span className="text-[28px] font-semibold text-[#333333]">
            {summary?.orderCount ?? "—"}
          </span>
        </figure>
        <figure className="flex min-w-[200px] flex-1 flex-col gap-1 rounded-xl border bg-white-A700 p-5">
          <figcaption className="text-sm text-gray-600_01">
            Net commission
          </figcaption>
          <span className="text-[28px] font-semibold text-[#333333]">
            ₹{rupees(summary?.netMinor)}
          </span>
        </figure>
        {summary?.myProfitMinor !== undefined && (
          <figure className="flex min-w-[200px] flex-1 flex-col gap-1 rounded-xl border bg-white-A700 p-5">
            <figcaption className="text-sm text-gray-600_01">
              My profit
            </figcaption>
            <span className="text-[28px] font-semibold text-[#2E7D32]">
              ₹{rupees(summary.myProfitMinor)}
            </span>
          </figure>
        )}
        <figure className="flex min-w-[200px] flex-1 flex-col gap-1 rounded-xl border bg-white-A700 p-5">
          <figcaption className="text-sm text-gray-600_01">
            TDS withheld
          </figcaption>
          <span className="text-[28px] font-semibold text-[#333333]">
            ₹{rupees(summary?.tdsMinor)}
          </span>
        </figure>
        <figure className="flex min-w-[200px] flex-1 flex-col gap-1 rounded-xl border bg-white-A700 p-5">
          <figcaption className="text-sm text-gray-600_01">
            Still to pay{partnerName ? ` — ${partnerName}` : ""}
          </figcaption>
          <span className="text-[28px] font-semibold text-[#F22D2D]">
            ₹{rupees(summary?.pendingNetMinor)}
          </span>
          <span className="text-xs text-gray-500">
            {summary?.pendingCount ?? 0} lines
          </span>
        </figure>
      </div>

      {/* Bulk actions */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-gray-600_01">
          {selected.size > 0 ? `${selected.size} selected` : "Select rows to settle"}
        </span>
        <button
          type="button"
          disabled={selected.size === 0 || mark.isLoading}
          onClick={() =>
            mark.mutate({ ids: Array.from(selected), billed: true })
          }
          className="rounded-lg border border-[#165FE3] px-4 py-2 text-sm font-medium text-[#165FE3] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Mark billed
        </button>
        <button
          type="button"
          disabled={selected.size === 0 || mark.isLoading}
          onClick={() => mark.mutate({ ids: Array.from(selected), paid: true })}
          className="rounded-lg bg-[#165FE3] px-4 py-2 text-sm font-medium text-white-A700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Mark paid
        </button>
        <button
          type="button"
          disabled={selected.size === 0 || mark.isLoading}
          onClick={() =>
            mark.mutate({ ids: Array.from(selected), billed: false, paid: false })
          }
          className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-600_01 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Undo settlement
        </button>
        <button
          type="button"
          onClick={download}
          disabled={downloading || rows.length === 0}
          className="ml-auto rounded-lg border px-4 py-2 text-sm font-medium text-gray-600_01 disabled:cursor-not-allowed disabled:opacity-40"
          title="Downloads exactly what is on screen, without the profit column"
        >
          {downloading ? "Preparing…" : "Download"}
        </button>
        {notice && <span className="text-sm text-gray-600_01">{notice}</span>}
      </div>

      {/* The rows */}
      <div className="overflow-x-auto rounded-xl border bg-white-A700">
        <table className="w-full min-w-[1100px] text-left text-sm">
          <thead className="border-b bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-3">
                <input
                  type="checkbox"
                  id="payout-select-all"
                  aria-label="Select every row on this page"
                  checked={allOnPageSelected}
                  onChange={toggleAllOnPage}
                />
              </th>
              <th className="px-3 py-3">Order</th>
              <th className="px-3 py-3">Date</th>
              <th className="px-3 py-3">Agent</th>
              <th className="px-3 py-3">Customer</th>
              <th className="px-3 py-3">FCY amount</th>
              <th className="px-3 py-3">Purpose</th>
              <th className="px-3 py-3">Partner</th>
              <th className="px-3 py-3 text-right">Commission</th>
              <th className="px-3 py-3 text-right">TDS</th>
              <th className="px-3 py-3 text-right">Net</th>
              {showsMyProfit && (
                <th className="px-3 py-3 text-right">My profit</th>
              )}
              <th className="px-3 py-3">Billed</th>
              <th className="px-3 py-3">Paid</th>
            </tr>
          </thead>
          <tbody>
            {report.isLoading && (
              <tr>
                <td colSpan={showsMyProfit ? 14 : 13} className="px-3 py-12 text-center">
                  <Spinner />
                </td>
              </tr>
            )}
            {!report.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={showsMyProfit ? 14 : 13} className="px-3 py-12 text-center text-gray-500">
                  No commission in this window.
                </td>
              </tr>
            )}
            {rows.map((row: PayoutRow) => (
              <tr key={row.id} className="border-b last:border-0">
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    id={`payout-row-${row.id}`}
                    aria-label={`Select ${row.orderNo} for ${row.partner}`}
                    checked={selected.has(row.id)}
                    onChange={() => toggle(row.id)}
                  />
                </td>
                <td className="px-3 py-3">
                  <a
                    href={orderHref(row)}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-[#165FE3] hover:underline"
                  >
                    {row.orderNo}
                  </a>
                </td>
                <td className="whitespace-nowrap px-3 py-3">
                  {day(row.placedAt)}
                </td>
                <td className="px-3 py-3">{row.agent ?? "-"}</td>
                <td className="px-3 py-3">{row.customer ?? "-"}</td>
                <td className="whitespace-nowrap px-3 py-3 tabular-nums">
                  {fcy(row.fxAmountMinor, row.currency)}
                </td>
                <td className="px-3 py-3">{row.purpose ?? "-"}</td>
                <td className="px-3 py-3">{row.partner}</td>
                <td className="px-3 py-3 text-right tabular-nums">
                  {rupees(row.grossMinor)}
                </td>
                <td className="px-3 py-3 text-right tabular-nums">
                  {rupees(row.tdsMinor)}
                </td>
                <td className="px-3 py-3 text-right font-medium tabular-nums">
                  {rupees(row.netMinor)}
                </td>
                {showsMyProfit && (
                  <td className="px-3 py-3 text-right tabular-nums text-[#2E7D32]">
                    {rupees(row.myProfitMinor ?? null)}
                  </td>
                )}
                <td className="px-3 py-3">
                  <StateChip on={row.billed} yes="Billed" no="Open" />
                </td>
                <td className="px-3 py-3">
                  <StateChip on={row.paid} yes="Paid" no="Due" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paging */}
      <div className="flex items-center justify-between text-sm text-gray-600_01">
        <span>
          {report.data
            ? `Page ${report.data.page} of ${lastPage} · ${report.data.total} lines`
            : ""}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={(filters.page ?? 1) <= 1}
            onClick={() =>
              setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))
            }
            className="rounded-lg border px-3 py-1.5 disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={(filters.page ?? 1) >= lastPage}
            onClick={() =>
              setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))
            }
            className="rounded-lg border px-3 py-1.5 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function StateChip({
  on,
  yes,
  no,
}: {
  on: boolean;
  yes: string;
  no: string;
}): JSX.Element {
  return (
    <span
      className={`rounded-full px-2 py-1 text-xs font-medium ${
        on ? "bg-[#E8F6E8] text-[#2E7D32]" : "bg-gray-100 text-gray-600_01"
      }`}
    >
      {on ? yes : no}
    </span>
  );
}
